import React, { useMemo, useState } from 'react';
import { useApplications } from '../../hooks/useApplications';
import ApplicationStatusComponent from '../../components/ApplicationStatusComponent';
import ApproveApplicationDialog from '../../components/ApproveApplicationDialog';
import DeclineApplicationDialog from '../../components/DeclineApplicationDialog';
import ApplicationDetailsDialog from '../../components/ApplicationDetailsDialog';
import { employerApi } from '../../services/apiClient';
import { toast } from 'react-toastify';
import DynamicTable from '../../components/DynamicTable/DynamicTable';

// Status categories
const APPLICATION_STATUSES = [
  'PROCESSING',
  'AWAITING_EMPLOYER_SIGNATURE',
  'AWAITING_EMPLOYEE_SIGNATURE',
  'INFORMATION_REQUIRED',

];

const SUBSCRIPTION_STATUSES = [
  'ACTIVE',
  'COMPLETED',
  'PAUSED',
  'PENDING_PRODUCT_CHANGE',
  'AWAITING_ACTIVATION',
  'CANCELLED',
  'DECLINED'
];

const Applications = () => {
  const { applications, isLoading, error, refetch } = useApplications();
  const [selectedApplication, setSelectedApplication] = useState<any | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [approveGenerating, setApproveGenerating] = useState(false);

  // Split applications into two categories
  const { applicationsList, subscriptionsList } = useMemo(() => {
    if (!applications) return { applicationsList: [], subscriptionsList: [] };

    const apps = applications.filter((app: any) =>
      APPLICATION_STATUSES.includes(app.status)
    );
    const subs = applications.filter((app: any) =>
      SUBSCRIPTION_STATUSES.includes(app.status)
    );

    return {
      applicationsList: apps,
      subscriptionsList: subs,
    };
  }, [applications]);

  const applicationLookup = useMemo(() => {
    const map = new Map<string, any>();
    (applications || []).forEach((app: any) => {
      if (app?.id) {
        map.set(app.id, app);
      }
    });
    return map;
  }, [applications]);

  const handleDeclineConfirm = async (payload: { reasonCode: string; otherReason?: string }) => {
    if (!selectedApplication) {
      return;
    }

    try {
      await employerApi.post(`/applications/${selectedApplication.id}/decline`, payload);
      toast.success('Application declined successfully.');
      setDeclineModalOpen(false);
      setSelectedApplication(null);
    } catch (err: any) {
      console.error('Decline failed', err);
      throw new Error(err?.message || 'Unable to decline application.');
    }
  };

  const closeApproveModal = () => {
    setApproveModalOpen(false);
    setSelectedApplication(null);
  };

  const closeDeclineModal = () => {
    setDeclineModalOpen(false);
    setSelectedApplication(null);
  };

  const handleApproveClick = async (app: any) => {
    try {
      setSelectedApplication(app);

      // If contract already exists, just open the dialog
      const existingSlug = app?.docusealSubmission?.data?.[0]?.slug;
      if (existingSlug) {
        setApproveModalOpen(true);
        return;
      }

      // Otherwise, generate the DocuSeal contract first
      setApproveGenerating(true);
      setApproveModalOpen(true);

      const response: any = await employerApi.post(`/applications/${app.id}/start-approval`);
      const updatedApp = response?.application || { ...app, docusealSubmission: app.docusealSubmission };

      setSelectedApplication(updatedApp);
    } catch (err: any) {
      console.error('Error starting approval', err);
      toast.error(err?.message || 'Failed to prepare approval contract.');
      setApproveModalOpen(false);
      setSelectedApplication(null);
    } finally {
      setApproveGenerating(false);
    }
  };

  const openDetails = (app: any) => {
    setSelectedApplication(app);
    setDetailsModalOpen(true);
  };

  const buildRows = (items: any[]): any[] =>
    items.map(app => ([

      <p className="text-sm font-semibold text-black">
        {app.employee.firstName} {app.employee.surname}
      </p>
      ,
      app.creationDateTime,
      app.id,
      <div className="flex flex-row items-center gap-3" key={`${app.id}-benefit`}>
        {app.benefit.imageURL && (
          <img
            src={app.benefit.imageURL}
            className="w-6 h-6 rounded-full object-cover"
            alt={app.benefit.name}
          />
        )}
        <p>{app.benefit.name}</p>
      </div>,
      <ApplicationStatusComponent application={app} key={`${app.id}-status`} />,
      app.status === 'AWAITING_EMPLOYER_SIGNATURE' ? (
        <div className="flex flex-row gap-2 justify-end" key={`${app.id}-actions`}>
          <button
            className="px-3 py-1 rounded-lg bg-green-600 text-[#fff] text-sm font-semibold "
            onClick={(e) => {
              e.stopPropagation();
              handleApproveClick(app);
            }}
            disabled={approveGenerating && selectedApplication?.id === app.id}
          >
            {approveGenerating && selectedApplication?.id === app.id ? 'Preparing…' : 'Approve'}
          </button>
          <button
            className="px-3 py-1 rounded-lg bg-red text-white text-sm font-semibold"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedApplication(app);
              setDeclineModalOpen(true);
            }}
          >
            Decline
          </button>
        </div>
      ) : (
        <span className="text-xs text-gray-400" key={`${app.id}-actions`}>—</span>
      )
    ]));

  const applicationRows = useMemo(() => buildRows(applicationsList), [applicationsList, approveGenerating, selectedApplication]);
  const subscriptionRows = useMemo(() => buildRows(subscriptionsList), [subscriptionsList, approveGenerating, selectedApplication]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111]">Employees</h1>
        <p className="text-sm text-gray-500">
          Manage applications and subscriptions for your company's employees
        </p>
      </div>

      {error ? (
        <div className="bg-white border border-red-200 text-red-600 rounded-xl p-4 text-center">
          <p className="mb-2 font-semibold">Error loading applications. Please try again.</p>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-6 ">
          <div className="flex flex-row gap-2 border-4 p-2 bg-white border-orange-300 rounded-lg">
            <DynamicTable
              tableId="employer-applications"
              heading={`Applications (${applicationsList.length})`}
              cols={['Employee', 'Date Created', 'Application ID', 'Product', 'Status', 'Actions']}
              rows={applicationRows}
              loading={isLoading}
              // emptyMessage="No applications requiring action at this time."
              filterableColumns={['Status', 'Product']}
              onRowClicked={(applicationID: string) => {
                const app = applicationLookup.get(applicationID);
                if (app) openDetails(app);
              }}
              idIndexInColumns={2}
              showID={true} />
          </div>
          <div className="flex flex-row gap-2 border-4 p-2 bg-white border-green-300 rounded-lg">

            <DynamicTable
              tableId="employer-subscriptions"
              heading={`Subscriptions (${subscriptionsList.length})`}
              cols={['Employee', 'Date', 'Application ID', 'Product', 'Status', 'Actions']}
              rows={subscriptionRows}
              loading={isLoading}
              // emptyMessage="No active subscriptions at this time."
              filterableColumns={['Status', 'Product']}
              onRowClicked={(applicationID: string) => {
                const app = applicationLookup.get(applicationID);
                if (app) openDetails(app);
              }}
              idIndexInColumns={2}
              showID={true} />
          </div>
        </div>
      )}
      <ApproveApplicationDialog
        open={approveModalOpen}
        application={selectedApplication}
        onClose={closeApproveModal}
        generating={approveGenerating}
      />
      <DeclineApplicationDialog
        open={declineModalOpen}
        application={selectedApplication}
        onClose={closeDeclineModal}
        onConfirm={handleDeclineConfirm}
      />
      <ApplicationDetailsDialog
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedApplication(null);
        }}
        application={selectedApplication}
        onApplicationUpdated={() => {
          refetch();
        }}
      />
    </div>
  );
};

export default Applications;

